<template>
  <div class="container">
    <h1 class="titulo">Controle de Pacientes</h1>

    <!-- Filtros -->
    <div class="filtros">
      <select v-model="mesSelecionado">
        <option :value="0">Todos os Meses</option> <!-- ⬅️ aqui -->
        <option v-for="(mes, index) in meses" :key="index" :value="index + 1">
          {{ mes }}
        </option>
      </select>

      <select v-model="anoSelecionado" class="filtro-ano">
        <option :value="0">Ano</option>
        <option v-for="ano in anosDisponiveis" :key="ano" :value="ano">
          {{ ano }}
        </option>
      </select>
      <!-- filtro de Sistema -->
      <select v-model="sistemaSelecionado" class="filtro-sistema">
        <option value="">Todos os Sistemas</option>
        <option value="DIETSYSTEM">DietSystem</option>
        <option value="DOCTORALIA">Doctorália</option>
        <option value="WEBDIET">WebDiet</option>
        <option value="OUTROS">Outros</option>
      </select>
      <select v-model="ordenacaoSelecionada" class="filtro-ordenacao">
        <option value="">Ordem de criação</option>
        <option value="alfabetica">Ordem alfabética</option>
      </select>

      <input
        v-model="searchQuery"
        type="text"
        placeholder="Digite o nome do paciente"
        class="pesquisa-input"
      />
      <button class="btn-recarregar" title="Recarregar dados" @click="carregarDadosManualmente">
        ⟳
      </button>
    </div>

    <!-- Tabela (Desktop) -->
    <div class="tabela-container desktop-only">
      <table class="tabela">
        <thead>
          <tr>
            <th class="col-nome">Nome do Paciente</th>
            <th>Data de Nascimento</th>
            <th>Idade</th>
            <th>Indicação Clínica</th>
            <th>Telefone</th>
            <th class="col-email">Email</th>
            <th>Opções</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="paciente in pacientesFiltrados" :key="paciente.id">
            <td class="col-nome" data-label="Nome">{{ paciente.primeiro_nome }} {{ paciente.sobrenome }}</td>
            <td data-label="Nascimento">{{ formatarData(paciente.dn) }}</td>
            <td data-label="Idade">{{ paciente.idade }}</td>
            <td data-label="Indicação">{{ paciente.indicacao_clinica || 'Não informado' }}</td>
            <td data-label="Telefone">{{ paciente.telefone_formatado }}</td>
            <td class="col-email" data-label="Email">{{ paciente.email }}</td>
            <td data-label="Opções" class="opcoes">
              <button class="btn-visualizar" @click="visualizarPaciente(paciente)">👁️</button>
              <button class="btn-editar" @click="editarPaciente(paciente)">✏️</button>
              <button class="btn-excluir" @click="excluirPaciente(paciente.id)">🗑️</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Cards (Mobile) -->
    <div class="cards-container mobile-only">
      <div v-for="paciente in pacientesFiltrados" :key="'card-' + paciente.id" class="paciente-card">
        <div class="card-header">
          <span class="card-nome">{{ paciente.primeiro_nome }} {{ paciente.sobrenome }}</span>
          <span class="card-idade">{{ paciente.idade }} anos</span>
        </div>
        <div class="card-body">
          <div v-if="paciente.indicacao_clinica" class="card-info">
            <span class="card-label">Indicação:</span>
            <span>{{ paciente.indicacao_clinica }}</span>
          </div>
          <div v-if="paciente.telefone_formatado" class="card-info">
            <span class="card-label">Tel:</span>
            <a :href="'tel:' + paciente.telefone_formatado" class="card-link">{{ paciente.telefone_formatado }}</a>
          </div>
          <div v-if="paciente.email" class="card-info">
            <span class="card-label">Email:</span>
            <span class="card-email">{{ paciente.email }}</span>
          </div>
        </div>
        <div class="card-actions">
          <button class="btn-visualizar" @click="visualizarPaciente(paciente)">👁️ Ver</button>
          <button class="btn-editar" @click="editarPaciente(paciente)">✏️ Editar</button>
          <button class="btn-excluir" @click="excluirPaciente(paciente.id)">🗑️ Excluir</button>
        </div>
      </div>
    </div>

    <!-- Controles de Paginação -->
    <div v-if="paginacao.total > 0" class="paginacao">
      <button
        class="btn-paginacao"
        :disabled="paginacao.currentPage === 1"
        @click="irParaPagina(paginacao.currentPage - 1)"
      >
        ◀ Anterior
      </button>

      <span class="paginacao-info">
        Página {{ paginacao.currentPage }} de {{ paginacao.lastPage }}
        <span class="paginacao-total">• {{ paginacao.total }} pacientes</span>
      </span>

      <button
        class="btn-paginacao"
        :disabled="paginacao.currentPage === paginacao.lastPage"
        @click="irParaPagina(paginacao.currentPage + 1)"
      >
        Próxima ▶
      </button>

      <select
        :value="paginacao.perPage"
        class="select-itens"
        title="Itens por página"
        @change="alterarItensPorPagina(Number($event.target.value))"
      >
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>

    <!-- Modal de Paciente -->
    <!-- eslint-disable-next-line vue/no-v-model-argument vue/valid-v-model -->
    <PacienteModal
      v-model:mostrar="mostrarModal"
      v-model:paciente="pacienteSelecionado"
      :modo-edicao="modoEdicao"
      :modo-cadastro="modoCadastro"
      :pacientes-existentes="pacientes"
      @atualizar-paciente="atualizarPaciente"
      @cadastrar-paciente="aoCadastrarPaciente"
      @update:mostrar="val => {
        mostrarModal = val
        if (!val) fecharModal()
      }"
    />


    <LoadingOverlay v-if="isLoading" texto="Carregando pacientes..." />
    <p v-if="erro" class="error">Erro: {{ erro }}</p>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import PacienteModal from "./PacienteModal.vue";
import LoadingOverlay from "./LoadingOverlay.vue";
import Swal from 'sweetalert2'
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { usePacienteModal } from '@/composables/usePacienteModal'
import { prepararPaciente } from '@/utils/prepararPaciente'

// Router para filtros persistentes na URL
const route = useRoute();
const router = useRouter();


const {
  mostrarModal,
  modoEdicao,
  modoCadastro,
  pacienteSelecionado,
  abrirVisualizacao,
  abrirEdicao,
  abrirCadastro,
  fecharModal
} = usePacienteModal({
  sanitizePaciente: prepararPaciente
})

// Obter ano atual
const anoAtual = dayjs().year();

// Ler filtros da URL (query params) ou usar valores padrão
const getInitialFilter = (param, defaultValue, parser = (v) => v) => {
  const urlValue = route.query[param];
  return urlValue !== undefined ? parser(urlValue) : defaultValue;
};

/** @type {import('vue').Ref<import('@/types/api').Paciente[]>} */
const pacientes = ref([]);
const searchQuery = ref(getInitialFilter('busca', ''));
const mesSelecionado = ref(getInitialFilter('mes', 0, Number));
const anoSelecionado = ref(getInitialFilter('ano', anoAtual, Number));
const sistemaSelecionado = ref(getInitialFilter('sistema', ''));

// Anos disponíveis carregados uma única vez (não muda com filtros)
const anosCarregados = ref([anoAtual]);

// Retorna anos carregados (fixos após primeira carga)
const anosDisponiveis = computed(() => anosCarregados.value);

// Carrega anos disponíveis do banco (chamado uma vez no mount)
const carregarAnosDisponiveis = async () => {
  try {
    const response = await axios.get("/api/pacientes", {
      params: { per_page: 9999, campos: 'data_cadastro' },
      headers: { "Accept": "application/json" }
    });

    if (response.data?.data && Array.isArray(response.data.data)) {
      const anos = response.data.data
        .map(p => {
          const data = dayjs(p.data_cadastro);
          return data.isValid() ? data.year() : null;
        })
        .filter(ano => ano !== null && ano <= anoAtual);

      const todosAnos = new Set([...anos, anoAtual]);
      anosCarregados.value = Array.from(todosAnos).sort((a, b) => b - a);
    }
  } catch (e) {
    console.error('Erro ao carregar anos:', e);
  }
};
const ordenacaoSelecionada = ref(getInitialFilter('ordem', ''));

/** @type {import('vue').Ref<import('@/types/api').PaginacaoState>} */
const paginacao = ref({
  currentPage: getInitialFilter('pagina', 1, Number),
  lastPage: 1,
  total: 0,
  perPage: getInitialFilter('por_pagina', 25, Number),
  from: 0,
  to: 0
});

// Atualizar URL com os filtros atuais
const atualizarUrl = () => {
  const query = {};

  if (searchQuery.value) query.busca = searchQuery.value;
  if (mesSelecionado.value > 0) query.mes = mesSelecionado.value;
  if (anoSelecionado.value > 0) query.ano = anoSelecionado.value;
  if (sistemaSelecionado.value) query.sistema = sistemaSelecionado.value;
  if (ordenacaoSelecionada.value) query.ordem = ordenacaoSelecionada.value;
  if (paginacao.value.currentPage > 1) query.pagina = paginacao.value.currentPage;
  if (paginacao.value.perPage !== 25) query.por_pagina = paginacao.value.perPage;

  // Atualiza URL sem recarregar a página
  router.replace({ query });
};

// Estado para carregamento e erros (inicia true para mostrar loading imediatamente)
const isLoading = ref(true);
const erro = ref(null);


function editarPaciente(paciente) {
  abrirEdicao(paciente)
}

function visualizarPaciente(paciente) {
  abrirVisualizacao(paciente)
}




// 🟢 Após cadastrar, atualiza do backend
async function aoCadastrarPaciente(_novoPaciente) {
  isLoading.value = true;
  try {
    await fetchPacientes(true); // volta para página 1
    await Swal.fire("Sucesso!", "Paciente cadastrado com sucesso.", "success");
  } catch (err) {
    if (!err._handled) {
      await Swal.fire("Erro!", "Não foi possível atualizar a lista.", "error");
    }
  } finally {
    isLoading.value = false;
    modoCadastro.value = false;
  }
}

const carregarDadosManualmente = async () => {
  isLoading.value = true

  try {
    await fetchPacientes()

    Swal.fire({
      icon: 'success',
      title: 'Dados atualizados!',
      text: 'A lista de pacientes foi recarregada com sucesso.',
      timer: 2000,
      showConfirmButton: false
    })
  } catch (error) {
    if (!error._handled) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao atualizar',
        text: 'Não foi possível carregar os dados. Tente novamente.',
      })
    }
  } finally {
    isLoading.value = false
  }
}



const formatarData = (data) => {
  if (!data) return ""
  const parsed = dayjs(data)
  if (!parsed.isValid()) return ""
  if (parsed.isSame(dayjs("1900-01-01"))) return "Não informado" // 👈 ignora default
  return parsed.format("DD/MM/YYYY")
}

// Lista de meses
const meses = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];


/**
 * Busca pacientes da API com paginação e filtros.
 * @param {boolean} [resetPage=false] - Volta para a página 1
 * @returns {Promise<void>}
 */
const fetchPacientes = async (resetPage = false) => {
  isLoading.value = true;
  erro.value = null;

  if (resetPage) {
    paginacao.value.currentPage = 1;
  }

  try {
    const params = {
      page: paginacao.value.currentPage,
      per_page: paginacao.value.perPage
    };

    // Adiciona filtros apenas se preenchidos
    if (searchQuery.value) params.search = searchQuery.value;
    if (mesSelecionado.value > 0) params.mes = mesSelecionado.value;
    if (anoSelecionado.value > 0) params.ano = anoSelecionado.value;
    if (sistemaSelecionado.value) params.sistema = sistemaSelecionado.value;

    const response = await axios.get("/api/pacientes", {
      params,
      headers: { "Accept": "application/json" }
    });

    if (response.data?.data && Array.isArray(response.data.data)) {
      pacientes.value = response.data.data;
      paginacao.value.currentPage = response.data.current_page;
      paginacao.value.lastPage = response.data.last_page;
      paginacao.value.total = response.data.total;
      paginacao.value.from = response.data.from || 0;
      paginacao.value.to = response.data.to || 0;
    } else {
      pacientes.value = [];
    }
  } catch (error) {
    erro.value = error.response?.data?.message || error.message;
  } finally {
    isLoading.value = false;
  }
};

// Navegação de páginas
const irParaPagina = (page) => {
  if (page < 1 || page > paginacao.value.lastPage) return;
  paginacao.value.currentPage = page;
  fetchPacientes();
};

// Alterar itens por página
const alterarItensPorPagina = (quantidade) => {
  paginacao.value.perPage = quantidade;
  paginacao.value.currentPage = 1;
  fetchPacientes();
};

// Computed para ordenar pacientes (filtros agora são no servidor)
const pacientesFiltrados = computed(() => {
  if (!Array.isArray(pacientes.value)) return [];

  let lista = [...pacientes.value];

  // 🔀 Aplica ordenação local (alfabética)
  if (ordenacaoSelecionada.value === "alfabetica") {
    lista.sort((a, b) => {
      const nomeA = `${a.primeiro_nome} ${a.sobrenome}`.toLowerCase();
      const nomeB = `${b.primeiro_nome} ${b.sobrenome}`.toLowerCase();
      return nomeA.localeCompare(nomeB);
    });
  }

  return lista;
});

// 🔵 Atualiza um paciente existente na lista (modo edição)
const atualizarPaciente = async (_pacienteAtualizado) => {
  isLoading.value = true;
  try {
    await fetchPacientes(); // mantém página e filtros atuais
    await Swal.fire("Sucesso!", "Paciente atualizado com sucesso.", "success");
  } catch (err) {
    if (!err._handled) {
      await Swal.fire("Erro!", "Houve um erro ao atualizar a lista.", "error");
    }
  } finally {
    isLoading.value = false;
    modoEdicao.value = false;
  }
};



// ❌ Excluir Paciente
const excluirPaciente = async (id) => {
  const confirmacao = await Swal.fire({
    title: "Tem certeza?",
    text: "Essa ação não poderá ser desfeita.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#aaa",
    confirmButtonText: "Sim, excluir",
    cancelButtonText: "Cancelar"
  });

  if (!confirmacao.isConfirmed) return;

  // Cria o modal manualmente sem await
  Swal.fire({
    title: "Excluindo paciente...",
    didOpen: () => {
      Swal.showLoading();
    },
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false
  });

  try {
    await axios.delete(`/api/pacientes/${id}`, {
      headers: { Accept: "application/json" }
    });

    // Refetch para manter paginação consistente
    await fetchPacientes();

    Swal.close();
    Swal.fire("Excluído!", "Paciente excluído com sucesso.", "success");

  } catch (error) {
    Swal.close();
    if (!error._handled) {
      Swal.fire("Erro!", error.message, "error");
    }
  }
}


// Watchers para refetch quando filtros mudam
watch([mesSelecionado, anoSelecionado, sistemaSelecionado, ordenacaoSelecionada], () => {
  fetchPacientes(true); // resetPage = true
  atualizarUrl();
});

// Debounce para busca por texto (espera 500ms após digitar)
let searchTimeout = null;
watch(searchQuery, () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    fetchPacientes(true);
    atualizarUrl();
  }, 500);
});

// Atualizar URL quando página ou itens por página mudar
watch([() => paginacao.value.currentPage, () => paginacao.value.perPage], () => {
  atualizarUrl();
});

// Buscar pacientes ao montar o componente
onMounted(async () => {
  await carregarAnosDisponiveis();
  fetchPacientes();
  // Atualiza URL inicial se houver filtros padrão
  atualizarUrl();
});

// Expor funções externamente
defineExpose({
  abrirCadastro,
  recarregar: () => fetchPacientes(true)
});

</script>

<style scoped>

/* 📱 === ESTILO BASE (MOBILE FIRST) === */
@media (min-width: 0px) {    /* 📱 === MOBILE*/
  .swal2-loader {
    display: inline-block;
    width: 2em;
    height: 2em;
    border: 0.25em solid #ccc;
    border-top: 0.25em solid #3085d6;
    border-radius: 50%;
    animation: swal2-rotate 0.75s linear infinite;
    margin-top: 1em;
  }

  @keyframes swal2-rotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .btn-recarregar {
    background: #4caf50;
    color: white;
    width: 38px;
    height: 38px;
    min-width: 38px;
    border: none;
    border-radius: 8px;
    font-size: 20px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
  }

  .btn-recarregar:hover {
    background: #388e3c;
    transform: rotate(180deg);
  }

  .dark-mode .btn-recarregar {
    background: #388e3c;
  }

  .dark-mode .btn-recarregar:hover {
    background: #2e7d32;
  }

  .btn-verde {
    background: #4caf50;
    color: white;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.3s;
  }
  .btn-verde:hover {
    background: #388e3c;
  }

  .container {
    max-width: 95%;
    margin: 20px auto;
    text-align: center;
  }

  .filtros {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 15px;
  }

  .filtros select,
  .pesquisa-input {
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 5px;
    font-size: 14px;
    width: 100%;
  }

  .pesquisa-input {
    flex-grow: 1;
  }

  .btn-pesquisa {
    background: #4caf50;
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 5px;
    cursor: pointer;
  }

  /* Tabela */
  .tabela-container {
    overflow-x: auto;
    max-height: 500px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
  }

  .tabela-container table {
    border-collapse: collapse;
    width: 100%;
  }
  .tabela-container th {
    position: sticky;
    top: 0;
    background: #f9f9f9; /* ou branco */
    z-index: 1;
    padding: 8px;
    border-bottom: 2px solid #ccc !important;
  }

  .tabela {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  th,
  td {
    border-bottom: 1px solid #ddd;
    padding: 10px;
    white-space: nowrap;
    text-align: left;
  }

  th {
    background: #f8f8f8;
    font-weight: bold;
  }

  .dark-mode .tabela th,
  .dark-mode .tabela td {
    background-color: #1e1e1e !important;
    color: #f1f1f1 !important;
    border-bottom-color: #2a2a2a !important;
  }

  .dark-mode .tabela-container {
    border-color: #2a2a2a;
    background: #1e1e1e;
  }

  .dark-mode .tabela-container th {
    background: #252525 !important;
    border-bottom-color: #333 !important;
  }

  .opcoes {
    text-align: center;
  }

  .btn-visualizar,
  .btn-editar,
  .btn-excluir {
    font-size: 14px;
    padding: 5px;
    border-radius: 5px;
    cursor: pointer;
    margin-right: 5px;
  }

  .btn-visualizar {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid blue;
    color: blue;
  }

  .btn-editar {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #4caf50;
    color: #4caf50;
  }

  .btn-excluir {
    background:  rgba(255, 255, 255, 0.9);
    border: 1px solid red;
    color: red;
  }

  .erro {
    color: red;
    font-weight: bold;
    margin-bottom: 10px;
  }

  /* Paginação */
  .paginacao {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin: 15px 0;
    padding: 12px;
    background: #f9f9f9;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
  }

  .btn-paginacao {
    background: #4caf50;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.2s, opacity 0.2s;
  }

  .btn-paginacao:hover:not(:disabled) {
    background: #388e3c;
  }

  .btn-paginacao:disabled {
    background: #ccc;
    color: #888;
    cursor: not-allowed;
    opacity: 0.7;
  }

  .paginacao-info {
    font-size: 14px;
    color: #333;
    font-weight: 500;
  }

  .paginacao-total {
    color: #666;
    font-weight: normal;
  }

  .select-itens {
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 13px;
    background: white;
    cursor: pointer;
    margin-left: 5px;
  }

  /* Dark mode paginação */
  .dark-mode .paginacao {
    background: #2a2a2a;
    border-color: #444;
  }

  .dark-mode .paginacao-info {
    color: #f1f1f1;
  }

  .dark-mode .paginacao-total {
    color: #aaa;
  }

  .dark-mode .select-itens {
    background: #1e1e1e;
    color: #f1f1f1;
    border-color: #555;
  }

  /* Mobile: esconde tabela, mostra cards */
  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: block;
  }

  /* Card view para mobile */
  .cards-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 10px;
  }

  .paciente-card {
    background: #fff;
    border-radius: 10px;
    border: 1px solid #e0e0e0;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }

  .dark-mode .paciente-card {
    background: #1e1e1e;
    border-color: #333;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    background: #f8f8f8;
    border-bottom: 1px solid #e0e0e0;
  }

  .dark-mode .card-header {
    background: #252525;
    border-bottom-color: #333;
  }

  .card-nome {
    font-weight: 600;
    font-size: 15px;
    color: #333;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 8px;
  }

  .dark-mode .card-nome {
    color: #f1f1f1;
  }

  .card-idade {
    font-size: 13px;
    color: #666;
    white-space: nowrap;
    background: #e8f5e9;
    padding: 2px 8px;
    border-radius: 10px;
  }

  .dark-mode .card-idade {
    color: #95d5b2;
    background: #1b4332;
  }

  .card-body {
    padding: 10px 14px;
  }

  .card-info {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 3px 0;
    font-size: 13px;
    color: #555;
  }

  .dark-mode .card-info {
    color: #ccc;
  }

  .card-label {
    font-weight: 500;
    color: #888;
    min-width: 65px;
    flex-shrink: 0;
  }

  .dark-mode .card-label {
    color: #999;
  }

  .card-email {
    word-break: break-all;
  }

  .card-link {
    color: #2196f3;
    text-decoration: none;
  }

  .card-actions {
    display: flex;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid #e0e0e0;
  }

  .dark-mode .card-actions {
    border-top-color: #333;
  }

  .card-actions .btn-visualizar,
  .card-actions .btn-editar,
  .card-actions .btn-excluir {
    flex: 1;
    padding: 10px 8px;
    font-size: 13px;
    border-radius: 8px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  /* Título responsivo */
  .titulo {
    font-size: 20px;
    margin-top: 10px;
    margin-bottom: 8px;
  }
}

/* 💻 === DESKTOP PADRÃO (768px até 1023px) === */
@media (min-width: 768px) {    /*💻 === DESKTOP*/

  /* Desktop: mostra tabela, esconde cards */
  .desktop-only {
    display: block;
  }

  .mobile-only {
    display: none !important;
  }

  .paginacao {
    flex-direction: row;
    justify-content: center;
  }

  .container {
    max-width: 100%;
    margin: 0px auto;
    text-align: center;
  }

  .titulo {
    margin-bottom: 10px;
    font-size: 30px;

  }
  .filtros {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .filtros select,
  .pesquisa-input {
    width: 160px;
  }
  .filtro-ano {
    background-color: #fffb;
    color: #333;
    margin-left: 1px;
    max-width: 100px;
  }
  .filtro-sistema {
    padding: 6px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 12px;
    background-color: #fff8;
    color: #333;
    margin-left: 8px;
  }

  .filtro-ordenacao {
    padding: 6px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 12px;
    background-color: #fff;
    color: #333;
    margin-left: 8px;
  }

  /* Dark mode */
  .dark-mode .filtro-ordenacao {
    background-color: #1e1e1e;
    color: #f1f1f1;
    border-color: #555;
  }


  .tabela {
    font-size: 12px;
  }

  .tabela-container {
    margin: 10px 0px;
    max-height: 350px;
    overflow-y: auto;
  }

  .tabela-container table {
    border-collapse: collapse;
    width: 100%;
  }
  .tabela-container th {
    position: sticky;
    top: 0;
    z-index: 10; /* força ficar no topo */
    background-color: #f5f5f5; /* garante que cubra */
    box-shadow: inset 0 -2px 0 #f5f5f5; /* ✅ borda “falsa” para simular linha inferior */
    padding: 8px;
    text-align: left;
    font-size: 14px;
    font-weight: bold;
  }

  .tabela th,
  .tabela td {
    padding: 8px;
  }
  .dark-mode .tabela th,
  .dark-mode .tabela td {
    background-color: #1e1e1e !important;
    color: #f1f1f1 !important;
  }


  .col-nome {
    width: auto;
    max-width: 280px;
    min-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }


  .btn-visualizar,
  .btn-editar,
  .btn-excluir {
    font-size: 13px;
    padding: 4px 6px;
  }
}

/* 🖥️ === TELONAS (DESKTOP GRANDE ≥ 1600px) === */
@media (min-width: 1810px) {  /* 🖥️ === TELONAS */
  .container {
    max-width: 90%;
    margin: 0px auto;
    text-align: center;
  }
  .titulo {
    margin-bottom: 10px;
    font-size: 48px;

  }
  .tabela-container {
    max-width: 100%;
    max-height: 500px;
    margin: 0 auto;
    overflow-y: auto;
  }
  .tabela-container table {
    border-collapse: collapse;
    width: 100%;
  }
  .tabela-container th {
    position: sticky;
    top: 0;
    z-index: 10; /* força ficar no topo */
    background-color: #ccc; /* garante que cubra */
    box-shadow: inset 0 -2px 0 #ccc; /* ✅ borda “falsa” para simular linha inferior */
    padding: 8px;
    text-align: left;
    font-size: 18px;
    font-weight: bold;
  }



  .tabela {
    font-size: 15px;
    table-layout: fixed;
  }

  .tabela th,
  .tabela td {
    padding: 6px 8px;
  }
  .dark-mode .tabela th,
  .dark-mode .tabela td {
    background-color: #1e1e1e !important;
    color: #f1f1f1 !important;
  }

  .col-nome {
    width: 100%;
    max-width: 25%;
    min-width: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .col-email {
    width: 260px;
    max-width: 260px;
    min-width: 220px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }



  .btn-visualizar,
  .btn-editar,
  .btn-excluir {
    font-size: 18px;
    padding: 3px 5px;
  }

  .filtros select,
  .pesquisa-input {
    font-size: 16px;
    padding: 6px;
    width: 180px;
  }
  .filtro-ordenacao {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  background-color: #fff;
  color: #333;
  margin-left: 8px;
  }

  /* Dark mode */
  .dark-mode .filtro-ordenacao {
    background-color: #1e1e1e;
    color: #f1f1f1;
    border-color: #555;
  }


  .btn-pesquisa {
    font-size: 13px;
    padding: 6px 10px;
  }
}

</style>

